<template>
  <div class="cx-measured-points-filters">
    <div class="cx-measured-points-filters__form">
      <transition :name="applyAnimations('filter2')">
        <div v-show="isAnimationReady">
          <cx-filter-title
            :title="translations.measurementDate"
            :rank="4"/>
          <cx-time-range-filter
            :startDate="startDate"
            :changeStartDate="changeStartDate"
            :endDate="endDate"
            :changeEndDate="changeEndDate"
            :projectStartDay="projectStartDay"
            :events="activeDays"
            :loading="activeDaysLoading"/>
        </div>
      </transition>
      <transition :name="applyAnimations('filter2')">
        <div
          class="cx-measured-points-filters__filter-row"
          v-show="isAnimationReady">
          <cx-filter-title
            :title="translations.mainFilters"
            :rank="4"/>
          <cx-reference-model-filter
            :refmodels="allModels"
            :selectedRefModel="selectedRefModel"
            :changeSelectedRefModel="changeSelectedRefModel"/>
        </div>
      </transition>
      <transition :name="applyAnimations('filter2')">
        <div
          class="cx-measured-points-filters__filter-row"
          v-show="isAnimationReady">
          <cx-unit-type-filter
            :availableUnitTypes="availableUnitTypes"
            :selectedUnitTypes="selectedUnitTypes"
            :changeSelectedUnitTypes="changeSelectedUnitTypes"
            ref="unitTypeFilter"/>
          <cx-unit-name-filter
            :availableUnits="availableUnitNames"
            :selectedUnits="selectedUnitNames"
            :updateSelection="changeUnitNames"/>
        </div>
      </transition>
      <transition :name="applyAnimations('filter2')">
        <div
          class="cx-measured-points-filters__filter-row"
          v-show="isAnimationReady">
          <cx-checkbox
            v-model:checked="autologPointsChecked"
            :label="translations.autolog"/>
          <cx-checkbox
            v-model:checked="discardedPointsChecked"
            :label="translations.includeDiscarded"/>
        </div>
      </transition>
      <transition :name="applyAnimations('filter3')">
        <div
          class="cx-measured-points-filters__filter-row"
          v-if="availableMetadataFilters.length > 0"
          v-show="isAnimationReady">
          <cx-filter-title
            :title="translations.metadataFilters"
            :rank="4"/>
          <cx-metadata-filters
            :availableMetadataFilters="availableMetadataFilters"
            :addedMetadataFilters="addedMetadataFilters"
            :addMetadataFilter="addMetadataFilter"
            :removeMetadataFilter="removeMetadataFilter"
            :changeMetadataFilterValues="changeMetadataFilterValues"
            :changeMetadataFilterComparators="changeMetadataFilterComparators"/>
        </div>
      </transition>
      <transition :name="applyAnimations('filter4')">
        <div
          class="cx-measured-points-filters__buttons"
          v-show="isAnimationReady">
          <cx-button
            :disabled="false"
            @click="onClearFiltersClickedHandler"
            variantSecondary
            :tooltip="translations.clearFilters">
            {{ translations.clearFilters }}
          </cx-button>
          <cx-button
            :isDisabled="!isFormValid || !datesValid"
            @click="search"
            variantPrimary
            :tooltip="translations.search"
            :loading="searching">
            {{ translations.search }}
          </cx-button>
        </div>
      </transition>
      <div class="cx-measured-points-filters__results">
        <transition :name="applyAnimations('filter5')">
          <cx-list
            :items="results"
            :header="listHeader"
            v-show="isAnimationReady">
            <template #default="{ item, selected }">
              <cx-list-item
                :item="item"
                :selected="selected"
                :disabled="true">
                <template #selection><div></div></template>
                <template #before-content="{}">
                  <cx-icon
                    icon="cx_points"
                    :size="15"
                    class="cxu-color-faded"/>
                </template>
              </cx-list-item>
            </template>
            <template #header-append="{}">
              <cx-warning
                :warning="translations.warningHeader || ''"
                v-if="isLoadedPointsLimitExceeded"/>
            </template>
            <template #placeholder>
              <cx-label
                class="cx-simple-list__placeholder results-placeholder"
                :label="translations.noResult"/>
            </template>
          </cx-list>
        </transition>
      </div>
      <transition :name="applyAnimations('action')">
        <cx-filter-action-panel
          v-show="isAnimationReady"
          :onApplyClicked="onApplyClickedHandler"
          :applyText="translations.display"
          :applyTooltip="translations.display"
          :isDisabled="!results.length"
          :queryOngoing="queryOngoing"
          :isClearFiltersVisible="false">
          <cx-menu-button
            :text="translations.download"
            :items="[{ name: 'csv' }, { name: 'json' }, { name: 'geo' }]"
            :loading="isExportLoading"
            :disabled="!results.length"
            menuPosition="top"
            @click="onExportClickedHandler"/>
        </cx-filter-action-panel>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useVuex } from '@vueblocks/vue-use-vuex';
import { endOfDay, endOfToday, isBefore, max, startOfDay, startOfToday } from 'date-fns';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { computed, ComputedRef, onMounted, ref, Ref } from 'vue';
import { useStore } from 'vuex';

import CxFilterActionPanel from '../common/CxFilterActionPanel.vue';
import CxFilterTitle from '../common/CxFilterTitle.vue';
import CxMetadataFilters from './components/CxMetadataFilters.vue';
import CxReferenceModelFilter from './components/CxReferenceModelFilter.vue';
import CxUnitNameFilter from './components/CxUnitNameFilter.vue';
import CxTimeRangeFilter from '@/app/filterPanel/common/CxTimeRangeFilter.vue';
import CxUnitTypeFilter from '@/app/filterPanel/common/CxUnitTypeFilter.vue';
import CxButton from '@/components/CxButton.vue';
import CxCheckbox from '@/components/CxCheckbox.vue';
import CxIcon from '@/components/CxIcon.vue';
import CxLabel from '@/components/CxLabel.vue';
import CxWarning from '@/components/CxWarning.vue';
import CxList from '@/components/list/CxList.vue';
import CxListItem from '@/components/list/CxListItem.vue';
import CxMenuButton from '@/components/menuComponents/CxMenuButton.vue';
import useAnimation from '@/composables/useAnimation';
import useLicence from '@/composables/useLicence';
import useWaffleFlag from '@/composables/useWaffleFlag';
import { PointSet } from '@/domain/pointSet/PointSet';
import { RefModel } from '@/domain/refmodels/RefModel';
import { Unit } from '@/domain/units/Unit';
import { UnitTypes } from '@/domain/units/UnitTypes.enum';
import pointApi from '@/http/point.api';
import analyticsService from '@/services/analytics.service';
import translationService from '@/services/translation.service';
import FilterMeasuredPointViewModel from '@/store/filterModule/points/modules/filterMeasuredPointViewModel';
import { UseActions, UseMutations } from '@/store/UseVuex.types';
import { gettext } from '@/translations/gettext.setup';

const store = useStore();
const { $gettext, interpolate } = gettext;
const { applyAnimations, isAnimationReady } = useAnimation('CxMeasuredPointFilters');
const isFormValid: Ref<boolean> = ref(false);
const unitTypeFilter: Ref<any> = ref(null);
const searching: Ref<boolean> = ref(false);
const notificationsStore = useVuex('notifications', store);
const { error } = notificationsStore.useActions(['error']) as UseActions;

const filterMeasuredPointStore = useVuex('filterPoints/filterMeasuredPoint', store);
const { selectedRefModel } = filterMeasuredPointStore.useGetters(['selectedRefModel']) as {
  selectedRefModel: ComputedRef<RefModel>;
};
const { changeSelectedRefModel } = filterMeasuredPointStore.useMutations(['changeSelectedRefModel']) as UseMutations;

const appStore = useVuex('app', store);
const { allModels, allUnits, projectStartDay } = appStore.useGetters(['allModels', 'allUnits', 'projectStartDay']) as {
  allModels: Ref<RefModel[]>;
  allUnits: Ref<Unit[]>;
  projectStartDay: Ref<string>;
};

const mapStore = useVuex('map', store);
const { maxLoadablePointsCount } = mapStore.useGetters(['maxLoadablePointsCount']) as {
  maxLoadablePointsCount: Ref<number>;
};
const { startDate, endDate } = filterMeasuredPointStore.useGetters(['startDate', 'endDate']) as {
  startDate: Ref<string>;
  endDate: Ref<string>;
};
const { changeEndDate, changeStartDate } = filterMeasuredPointStore.useMutations([
  'changeEndDate',
  'changeStartDate',
]) as UseMutations;
const { isPartialPointSet, isLoadedPointsLimitExceeded, resultPointSet } = filterMeasuredPointStore.useGetters([
  'isPartialPointSet',
  'isLoadedPointsLimitExceeded',
  'resultPointSet',
]) as {
  isPartialPointSet: Ref<boolean>;
  isLoadedPointsLimitExceeded: Ref<boolean>;
  resultPointSet: Ref<FilterMeasuredPointViewModel[]>;
};

const pointSet = computed((): PointSet | null => {
  if (resultPointSet.value.length) return resultPointSet.value[0].data;
  return null;
});

type MeasuredPointFiltersResult = {
  id?: string;
  title?: string;
  warning?: string;
};
const getResultsFromStore = (): MeasuredPointFiltersResult[] => {
  if (resultPointSet.value && resultPointSet.value.length) {
    return resultPointSet.value.map((r) => ({ id: r.id, title: r.name, warning: r.isResultWarning }));
  }
  return [];
};
const results = ref<MeasuredPointFiltersResult[]>(getResultsFromStore());

const getWarningHeader = (): string | null => {
  if (!pointSet.value) return null;
  // As we don't use partial point set concept for UI, we fall back to bulk performance limit warning message.
  if (!isLoadedPointsLimitExceeded.value && !isPartialPointSet.value) return null;
  const title = $gettext('Unable to load %{ totalCount } points because they exceed the performance limit (%{ max }).');
  return interpolate(title, {
    totalCount: pointSet.value.totalCount,
    max: maxLoadablePointsCount.value,
  });
};
const translations = computed(() => {
  return {
    clearFilters: $gettext('Clear Filters'),
    search: $gettext('Search'),
    measurementDate: $gettext('Measurement Date'),
    mainFilters: $gettext('Main Filters'),
    metadataFilters: $gettext('Metadata Filters'),
    download: $gettext('Save Point Data As...'),
    warningHeader: getWarningHeader(),
    autolog: $gettext('Include Autologged Points'),
    includeDiscarded: $gettext('Include Deleted Points'),
    display: $gettext('Display'),
    result: $gettext('Results'),
    noResult: $gettext('No results'),
    errorMessage: $gettext(
      'Fetching data for Measured Points Filtering Panel failed. Try reopening the panel or contact support.'
    ),
  };
});

const projectUuid: ComputedRef<string> = computed(() => store.getters.projectUuid);

const activeDaysLoading: Ref<boolean> = ref(false);
const activeDays: Ref<Date[]> = ref([]);
const fetchActiveDays = async () => {
  activeDaysLoading.value = true;
  try {
    activeDays.value = await pointApi.getActiveDays(projectUuid.value);
    if (!startDate.value || !endDate.value) {
      if (activeDays.value.length > 0) {
        const dateFrom = startOfDay(max(activeDays.value));
        const dateTo = endOfDay(dateFrom);
        changeStartDate(dateFrom);
        changeEndDate(dateTo);
      } else {
        changeStartDate(startOfToday());
        changeEndDate(endOfToday());
      }
    }
  } catch (err) {
    error({ exception: err, message: translations.value.errorMessage });
  } finally {
    activeDaysLoading.value = false;
  }
};

const listHeader = computed((): string => {
  return results.value.length ? translations.value.result : translations.value.noResult;
});

const { selectedUnitNames, selectedUnitTypes, availableUnitNames } = filterMeasuredPointStore.useGetters([
  'selectedUnitNames',
  'selectedUnitTypes',
  'availableUnitNames',
]) as {
  selectedUnitNames: Ref<Unit[]>;
  selectedUnitTypes: Ref<UnitTypes[]>;
  availableUnitNames: Ref<Unit[]>;
};

const { changeUnitNames } = filterMeasuredPointStore.useMutations(['changeUnitNames']) as UseMutations;
const { changeSelectedUnitTypes } = filterMeasuredPointStore.useActions(['changeSelectedUnitTypes']) as UseActions;
const availableUnitTypes = computed((): { id: UnitTypes; name: string } => {
  const uniqueTypeUnits = _.uniqBy(allUnits.value, 'entityType');
  return uniqueTypeUnits.map((u) => {
    return {
      id: u.entityType,
      name: translationService.get(u.entityType),
    };
  });
});

const { availableMetadataFilters, addedMetadataFilters } = filterMeasuredPointStore.useGetters([
  'availableMetadataFilters',
  'addedMetadataFilters',
]) as {
  availableMetadataFilters: Ref<{ fieldName: string; dataType: string; displayName: string }[]>;
  addedMetadataFilters: Ref<string[]>;
};

const { removeMetadataFilter, changeMetadataFilterValues, changeMetadataFilterComparators } =
  filterMeasuredPointStore.useMutations([
    'removeMetadataFilter',
    'changeMetadataFilterValues',
    'changeMetadataFilterComparators',
  ]) as UseMutations;

const { addMetadataFilter, exportPoints } = filterMeasuredPointStore.useActions([
  'addMetadataFilter',
  'exportPoints',
]) as UseActions;

const { queryOngoing, isExportLoading } = filterMeasuredPointStore.useGetters(['queryOngoing', 'isExportLoading']) as {
  queryOngoing: Ref<boolean>;
  isExportLoading: Ref<boolean>;
};

const datesValid = computed((): boolean => {
  return isBefore(new Date(startDate.value), new Date(endDate.value));
});

const { includeAutologPoints, includeDiscardedPoints } = filterMeasuredPointStore.useGetters([
  'includeAutologPoints',
  'includeDiscardedPoints',
]) as {
  includeAutologPoints: Ref<boolean>;
  includeDiscardedPoints: Ref<boolean>;
};
const { clearFilters, setIncludeAutologPoints, setIncludeDiscardedPoints } = filterMeasuredPointStore.useMutations([
  'clearFilters',
  'setIncludeAutologPoints',
  'setIncludeDiscardedPoints',
]) as UseMutations;
const { displayMeasuredPoints, searchMeasuredPoints } = filterMeasuredPointStore.useActions([
  'displayMeasuredPoints',
  'searchMeasuredPoints',
]) as UseActions;
const autologPointsChecked = computed({
  get() {
    return includeAutologPoints.value;
  },
  set(value) {
    setIncludeAutologPoints(value);
  },
});
const discardedPointsChecked = computed({
  get() {
    return includeDiscardedPoints.value;
  },
  set(value) {
    setIncludeDiscardedPoints(value);
  },
});
const onClearFiltersClickedHandler = () => {
  clearFilters();
  results.value = [];
  unitTypeFilter.value.clearChipsInput();
};

type MeasuredPointSearchEventData = {
  includeAutologPoints: boolean;
  includeDeletedPoints: boolean;
  metadataFiltersSelected: string[];
  metadataFiltersCount: number;
  timestamp: string;
};

const prepareMeasuredPointSearchEvent = (): MeasuredPointSearchEventData => {
  const metadataFilters = addedMetadataFilters.value;
  const filterFieldNames = Array.isArray(metadataFilters)
    ? metadataFilters.map((filter) => (typeof filter === 'string' ? filter : filter.fieldName || ''))
    : [];

  return {
    includeAutologPoints: includeAutologPoints.value,
    includeDeletedPoints: includeDiscardedPoints.value,
    metadataFiltersSelected: filterFieldNames,
    metadataFiltersCount: filterFieldNames.length,
    timestamp: new Date().toISOString(),
  };
};

const search = async () => {
  searching.value = true;
  try {
    const analyticsEventData = prepareMeasuredPointSearchEvent();
    analyticsService.trackMeasuredPointSearch(analyticsEventData);

    const total_count = await searchMeasuredPoints();

    if (total_count > 0) {
      results.value = [
        {
          id: uuidv4(),
          title: `${total_count} points`,
        },
      ];
    } else {
      results.value = [];
    }
  } catch (err) {
    error({ exception: err, message: translations.value.errorMessage });
  } finally {
    searching.value = false;
  }
};

const onApplyClickedHandler = () => {
  displayMeasuredPoints();
};

const onExportClickedHandler = (selectedExtension: { name: string }) => {
  analyticsService.trackMeasuredPointSaveFromFilterPanelAs({
    format: selectedExtension.name,
    timestamp: new Date().toISOString(),
  });
  exportPoints(selectedExtension.name);
};

onMounted(async () => {
  await fetchActiveDays();
});

defineExpose({
  getWarningHeader,
  fetchActiveDays,
  prepareMeasuredPointSearchEvent,
  onExportClickedHandler,
});
</script>

<style lang="less">
@import '../../../common';

.cx-measured-points-filters {
  display: flex;
  flex-direction: column;
  margin-top: @inputHeight;

  .cx-measured-points-filters__form {
    display: flex;
    flex-direction: column;

    .cx-measured-points-filters__filter-row {
      .size(100%, auto);
      padding-top: 25px;

      .cx-popper-content {
        overflow-y: auto;
      }
    }

    .cx-measured-points-filters__form-placeholder {
      height: 300px;
    }

    .cx-measured-points-filters__buttons {
      margin-top: 8px;
      display: flex;
      justify-content: flex-end;
      gap: 5px;
    }
  }

  .cx-measured-points-filters__results {
    display: flex;
    padding-top: 30px;

    .results-placeholder {
      text-align: left;
    }
  }

  .cx-measurement-date-filter {
    margin: 5px 0 0 0;
  }

  .cx-date-picker.cx-date-time-picker {
    flex: 1 1 auto;
  }

  .n-cx-select-display-settings {
    padding-left: 10px;
    font-size: 16px;
    font-weight: 100;
    color: rgba(255, 255, 255, 0.4);
  }
  .cx-filter-action-panel__button--export {
    .cx-menu.top {
      top: -82px;
    }
  }
}
</style>
